import * as Y from "yjs";

import {
  canManageCommentThread,
  createCommentReply,
  createCommentThread,
  type CreateCommentThreadInput,
  type ReplyToCommentThreadInput
} from "./comments.js";
import type { CommentMessage, CommentThread } from "./types.js";

export const ROOT_COMMENTS_KEY = "comments";

export function getCommentThreadsCollection(doc: Y.Doc): Y.Array<Y.Map<unknown>> {
  return doc.getArray(ROOT_COMMENTS_KEY);
}

export function serializeCommentThreads(doc: Y.Doc): CommentThread[] {
  return getCommentThreadsCollection(doc)
    .toArray()
    .map((thread) => serializeCommentThread(thread));
}

export function bootstrapCommentThreads(doc: Y.Doc, threads: CommentThread[]): void {
  const collection = getCommentThreadsCollection(doc);
  if (collection.length > 0 || threads.length === 0) {
    return;
  }

  collection.push(threads.map((thread) => commentThreadToYMap(thread)));
}

export function createCommentThreadInDoc(
  doc: Y.Doc,
  input: CreateCommentThreadInput,
  origin?: unknown
): CommentThread {
  const thread = createCommentThread(input);

  doc.transact(() => {
    getCommentThreadsCollection(doc).push([commentThreadToYMap(thread)]);
  }, origin);

  return thread;
}

export function replyToCommentThreadInDoc(
  doc: Y.Doc,
  input: ReplyToCommentThreadInput,
  origin?: unknown
): CommentMessage {
  const located = findThread(doc, input.threadId);
  if (!located) {
    throw new Error(`Comment thread "${input.threadId}" was not found.`);
  }

  const reply = createCommentReply(input);
  doc.transact(() => {
    const messages = getThreadMessages(located.thread);
    messages.push([commentMessageToYMap(reply)]);
    located.thread.set("updatedAt", reply.createdAt);
  }, origin);

  return reply;
}

export function archiveCommentThreadInDoc(
  doc: Y.Doc,
  threadId: string,
  actorClientId: string,
  origin?: unknown
): void {
  const located = findThread(doc, threadId);
  if (!located) {
    throw new Error(`Comment thread "${threadId}" was not found.`);
  }

  const thread = serializeCommentThread(located.thread);
  if (!canManageCommentThread(thread, actorClientId)) {
    throw new Error("Only the thread owner can archive this comment thread.");
  }

  const now = new Date().toISOString();
  doc.transact(() => {
    located.thread.set("status", "archived");
    located.thread.set("archivedAt", now);
    located.thread.set("updatedAt", now);
  }, origin);
}

export function deleteCommentThreadInDoc(
  doc: Y.Doc,
  threadId: string,
  actorClientId: string,
  origin?: unknown
): void {
  const located = findThread(doc, threadId);
  if (!located) {
    throw new Error(`Comment thread "${threadId}" was not found.`);
  }

  const thread = serializeCommentThread(located.thread);
  if (!canManageCommentThread(thread, actorClientId)) {
    throw new Error("Only the thread owner can delete this comment thread.");
  }

  doc.transact(() => {
    getCommentThreadsCollection(doc).delete(located.index, 1);
  }, origin);
}

function findThread(doc: Y.Doc, threadId: string): { index: number; thread: Y.Map<unknown> } | null {
  const threads = getCommentThreadsCollection(doc).toArray();

  for (let index = 0; index < threads.length; index += 1) {
    const thread = threads[index];
    if (thread.get("id") === threadId) {
      return { index, thread };
    }
  }

  return null;
}

function commentThreadToYMap(thread: CommentThread): Y.Map<unknown> {
  const map = new Y.Map<unknown>();
  map.set("id", thread.id);
  map.set("ownerClientId", thread.owner.clientId);
  map.set("ownerDisplayName", thread.owner.displayName);
  map.set("target", JSON.stringify(thread.target));
  map.set("status", thread.status);
  map.set("createdAt", thread.createdAt);
  map.set("updatedAt", thread.updatedAt);
  if (thread.archivedAt) {
    map.set("archivedAt", thread.archivedAt);
  }

  const messages = new Y.Array<Y.Map<unknown>>();
  messages.push(thread.messages.map((message) => commentMessageToYMap(message)));
  map.set("messages", messages);
  return map;
}

function commentMessageToYMap(message: CommentMessage): Y.Map<unknown> {
  const map = new Y.Map<unknown>();
  map.set("id", message.id);
  map.set("authorClientId", message.author.clientId);
  map.set("authorDisplayName", message.author.displayName);
  map.set("body", message.body);
  map.set("createdAt", message.createdAt);
  return map;
}

function serializeCommentThread(thread: Y.Map<unknown>): CommentThread {
  return {
    id: String(thread.get("id") ?? ""),
    owner: {
      clientId: String(thread.get("ownerClientId") ?? ""),
      displayName: String(thread.get("ownerDisplayName") ?? "Unknown")
    },
    target: JSON.parse(String(thread.get("target") ?? "{}")),
    status: thread.get("status") === "archived" ? "archived" : "active",
    createdAt: String(thread.get("createdAt") ?? ""),
    updatedAt: String(thread.get("updatedAt") ?? ""),
    archivedAt:
      typeof thread.get("archivedAt") === "string" ? String(thread.get("archivedAt")) : undefined,
    messages: getThreadMessages(thread).toArray().map((message) => ({
      id: String(message.get("id") ?? ""),
      author: {
        clientId: String(message.get("authorClientId") ?? ""),
        displayName: String(message.get("authorDisplayName") ?? "Unknown")
      },
      body: String(message.get("body") ?? ""),
      createdAt: String(message.get("createdAt") ?? "")
    }))
  };
}

function getThreadMessages(thread: Y.Map<unknown>): Y.Array<Y.Map<unknown>> {
  const messages = thread.get("messages");
  if (messages instanceof Y.Array) {
    return messages as Y.Array<Y.Map<unknown>>;
  }

  const nextMessages = new Y.Array<Y.Map<unknown>>();
  thread.set("messages", nextMessages);
  return nextMessages;
}
